import { all, call, put, select, spawn } from 'typed-redux-saga'
import Toast from 'react-native-toast-message'

import { navigationRef as Nav } from 'APP/Nav'
import * as NestedNavHelper from 'APP/Nav/NestedNavHelper'
import { SagaActionProps } from 'APP/Models/Actions'
import { loginState } from 'APP/Sagas/AuthSagas'
import I18n from 'APP/I18n'
import Scribe from 'APP/Services/Scribe'
import {
  fetchScribeV2User,
  fetchScribeV2UserEligibilities,
  patientState,
} from 'APP/Sagas/PatientSagas'
import { contentSagas } from 'APP/Store/Content'
import { eligibilitySearchActions, eligibilitySearchSelectors } from './index'
import Analytics from 'APP/Services/Analytics'
import { trackDdEvent } from 'APP/Lib/Datadog'
import { REGISTRATION_ACTIONS } from 'APP/Lib/Datadog/constants'

export function* checkEligibilityAccess() {
  try {
    const login = yield* select(loginState)
    const scribe = Scribe.create(login)
    const hasAccess = yield* call(scribe.hasRemainingOrganizationSearchRetries)
    if (!hasAccess) {
      yield call(NestedNavHelper.replace, 'eligibilityClaimScreen')
    }
    yield put(eligibilitySearchActions.checkEligibilityAccessSuccess())
  } catch (e) {
    yield put(eligibilitySearchActions.checkEligibilityAccessFailure({ error: e as Error }))
  }
}

export function* searchOrganizations({ payload: query }: SagaActionProps<string>) {
  try {
    const login = yield* select(loginState)
    const scribe = Scribe.create(login)
    const results = yield* call(scribe.lookupOrganizations, query)
    const organizations = results ?? []

    const eventName =
      organizations.length === 0
        ? 'trust_based_eligibility_search_no_results'
        : 'trust_based_eligibility_search_results'
    yield call(Analytics.trackEvent, eventName, {})

    yield put(eligibilitySearchActions.searchOrganizationsSuccess(organizations))
    yield call(Nav.navigate, 'trustBasedEligibilityResults')
  } catch (e) {
    yield put(eligibilitySearchActions.searchOrganizationsFailure({ error: e as Error }))
    yield call(Nav.navigate, 'eligibilityClaimScreen')
  }
}

export function* enrolWithOrganizationId({ payload: organizationId }: SagaActionProps<number>) {
  try {
    const login = yield* select(loginState)
    const patient = yield* select(patientState)
    const scribe = Scribe.create(login)

    if (!patient?.profile?.id) {
      throw new Error('Unable to enrol: missing patient profile id.')
    }

    const results = yield* select(eligibilitySearchSelectors.selectEligibilitySearchResults)
    const org = (results ?? []).find((o) => o.id === organizationId)
    if (!org) {
      throw new Error(
        `Unable to enrol: organization ${organizationId} not found in search results.`
      )
    }

    const userId = patient.profile.id
    yield* call(scribe.enrolByOrganizationId, userId, organizationId)
    yield* all([call(fetchScribeV2User), call(fetchScribeV2UserEligibilities)])
    const patientAfter = yield* select(patientState)
    yield* spawn(contentSagas.fetchLayout, { contentTags: patientAfter.profile.contentTags })

    const displayName = {
      en: org.display_name?.en ?? org.name,
      fr: org.display_name?.fr ?? org.name,
    }

    yield* call(Nav.reset, {
      index: 0,
      routes: [
        {
          name: 'claimAccessSuccessScreen',
          params: {
            displayNames: [displayName],
          },
        },
      ],
    })
    yield put(eligibilitySearchActions.enrolWithOrganizationIdSuccess())
    yield* call(trackDdEvent, REGISTRATION_ACTIONS.enrolledWithTrustBasedEligibility)
  } catch (e) {
    Toast.show({
      type: 'error',
      text1: I18n.t('Common.error.generic')!,
    })
    yield put(eligibilitySearchActions.enrolWithOrganizationIdFailure({ error: e as Error }))
  }
}
